import { useState, useRef, useEffect } from "react";
import { distanceToAbsolute, lerp }  from "$utils/math";
import { useSelector } from "react-redux";


export const ADSRGraph = ({instrument, adsrParameterNames, dynamicHighlight}) => {
    useEffect(()=> {
        if (dynamicHighlight) {
            document.addEventListener("midiNote-" + instrument.id , onMidiNote);
            () => document.removeEventListener("midiNote-" + instrument.id, onMidiNote)
        }
    }, []);

    const lastNoteInfoRef = useRef({});
    const onMidiNote = (evt) => {
        const {a, d, r} = adsrRef.current;
        let lastNoteInfo = lastNoteInfoRef.current;
        const {type, pitch} = evt.detail;
        if (type === "noteOff") {
            if (pitch !== lastNoteInfo.pitch && !instrument.isMono) return;
            lastNoteInfo = {stage: 'release', duration: r*1000};
        } else lastNoteInfo = {stage: 'pre-sustain', duration: (a+d)*1000, pitch};
        lastNoteInfo = {...lastNoteInfo, initTime: Date.now()};
        lastNoteInfoRef.current = lastNoteInfo;
        checkLastNoteStatus();
    }

    const [lastNoteStatus, setLastNoteStatus] = useState({stage: 'finished', progress: '1'})
    const checkLastNoteStatus = () => {
        const lastNoteInfo = lastNoteInfoRef.current;
        const elapsedTime = Date.now() - lastNoteInfo.initTime;
        const progress = Math.min(lastNoteInfo.duration === 0 ? 1 : elapsedTime / lastNoteInfo.duration, 1);
        let stage = lastNoteInfo.stage;
        if (progress === 1) {
            if (stage === 'pre-sustain') stage = 'sustain';
            if (stage === 'release') stage = 'finished';
        }
        setLastNoteStatus({progress, stage})
        if (progress < 1) setTimeout(checkLastNoteStatus, 30);
    }

    const a = useSelector(instrument.getDisplayParameterSelector(adsrParameterNames[0]));
    const d = useSelector(instrument.getDisplayParameterSelector(adsrParameterNames[1]));
    const s = useSelector(instrument.getDisplayParameterSelector(adsrParameterNames[2]));
    const r = useSelector(instrument.getDisplayParameterSelector(adsrParameterNames[3]));
    const adsrRef = useRef({});
    adsrRef.current = {a, d, s, r};

    const strokeWidthPx = 3;

    const timeValues = [a, d, r];

    const maxTime = 9; // knowing that the sum of the max values for attack, decay and release is 9. maybe it could get it automatically?
    const sustainTime = maxTime - timeValues.reduce((sum, element)=> sum + element);
    const timeValuesWithSustain = [a, d, sustainTime, r];

    const absoluteTimeValues = distanceToAbsolute(timeValuesWithSustain);
    const gainValues = [0, 1, s, s, 0];

    const adsrPoints = absoluteTimeValues.map((absTimeValue, index) => {
        const normTimeValue = absTimeValue / maxTime;
        const gainValue = gainValues[index];
        return {x: normTimeValue * (100 - strokeWidthPx / 2) + strokeWidthPx / 4, y: 75 - gainValue * 50}; // we account for stroke width so that the line isn't clipped
    });

    const sustainPoints = [adsrPoints[2], adsrPoints[3]];

    const adsrPathString = adsrPoints.reduce((pathString, point) => {
        return pathString + ` L ${point.x} ${point.y}`;
    }, `M ${strokeWidthPx/4} 75`);

    const gridSize = 4;
    let bgLineItems = [];
    for (let i = 1; i < gridSize; i++) {
        const crossAxisPos = i / (gridSize) * 100;
        const hLine = <line key={`bgHLine-${i}`} x1='0' x2='100' y1={crossAxisPos} y2={crossAxisPos} vectorEffect="non-scaling-stroke"/>
        const vLine = <line key={`bgVLine-${i}`} x1={crossAxisPos} x2={crossAxisPos} y1='100' y2='0' vectorEffect="non-scaling-stroke"/>
        bgLineItems.push(hLine, vLine);
    }

    const getEnvStatusLine = () => {
        const {stage, progress} = lastNoteStatus;
        if (stage === 'finished') return undefined;
        let endPoints = [{}, {}];
        if (stage === 'pre-sustain' || 'sustain') endPoints = [adsrPoints[0], sustainPoints[0]];
        if (stage === 'release') endPoints = [sustainPoints[1], adsrPoints[adsrPoints.length - 1]];
        const x = lerp(endPoints[0].x, endPoints[1].x, progress);
        const lastPoint = adsrPoints.reduce((result, current) => (current.x >= x || current.x < result.x) ? result : current);
        const nextPoint = adsrPoints[adsrPoints.indexOf(lastPoint)+1];
        const segmentDuration = nextPoint.x - lastPoint.x;
        const segmentProgress = segmentDuration === 0 ? 1 : (x - lastPoint.x) / segmentDuration;
        const y = lerp(lastPoint.y, nextPoint.y, segmentProgress);
        return <g>
            <rect x={x-20} y="25" width="20" height="50" vectorEffect="non-scaling-stroke" fill="url(#grad)" mask="url(#adsr-mask)"/>
            <circle cx={x} cy={y} r="0.9" fill="var(--accent-color)"/>
        </g>
    }

    const envStatusLine = getEnvStatusLine()
    return (
        <div className="adsr-graph">
            <svg viewBox={"0 0 100 100"} preserveAspectRatio="none">
                <g stroke="#555" strokeDasharray="1 4" strokeLinecap="round">
                    {bgLineItems}
                </g>

                <defs>
                    <mask id="sustain-mask">
                        <rect x="0" y="0" width="100" height="100" fill="white"/>
                        <g fill="black" stroke="black">
                            <line
                            x1={sustainPoints[0].x} x2={sustainPoints[1].x}
                            y1={sustainPoints[0].y} y2={sustainPoints[1].y}
                            vectorEffect="non-scaling-stroke" strokeWidth={strokeWidthPx}
                            strokeLinecap="round" />
                            <g fill="white" stroke="white">
                                <line
                                    x1={sustainPoints[0].x} x2={sustainPoints[1].x}
                                    y1={sustainPoints[0].y} y2={sustainPoints[1].y}
                                    vectorEffect="non-scaling-stroke" strokeWidth={strokeWidthPx}
                                    strokeLinecap="round" strokeDasharray="8"/>
                            </g>
                        </g>
                    </mask>
                    <mask id="adsr-mask">
                        <path d={adsrPathString + " Z"} fill="white" stroke="white" strokeWidth={strokeWidthPx} vectorEffect="non-scaling-stroke" strokeLinejoin="round"></path>
                    </mask>
                    <linearGradient id="grad">
                        <stop offset="0%" stopColor="transparent"/>
                        <stop offset="80%" stopColor="color-mix(in srgb, var(--accent-color) 10%, transparent)"/>
                        <stop offset="90%" stopColor="color-mix(in srgb, var(--accent-color) 30%, transparent)"/>
                        <stop offset="100%" stopColor="var(--accent-color)"/>
                    </linearGradient>
                </defs>

                <g fill="color-mix(in srgb, var(--accent-color) 10%, transparent)" stroke="var(--accent-color)" strokeWidth={strokeWidthPx} strokeLinecap="round" strokeLinejoin="round">
                    <path d={adsrPathString} vectorEffect="non-scaling-stroke" mask="url(#sustain-mask)" stroke="color-mix(in srgb, var(--accent-color) 90%, transparent)"></path>
                </g>
                {envStatusLine && envStatusLine}
            </svg>
        </div>
    )
}
